import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material'
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswerOutlined'
import { supabase } from '../../supabaseClient'
import { useAuth }  from '../../auth/AuthContext'
import PolishComposer from '../messaging/PolishComposer'

// Staff inbox for general student questions (student_message). Office staff see
// office-routed questions; mentors/instructors see mentor-routed questions from
// their own students. Reply in-app (emails the student), or dismiss.
//
// Dismiss exists because students send the same question to both their mentor
// and the office. The office copy then had no way to be cleared except by
// replying — emailing the student a second answer to a question already
// answered. A dismissed message is NOT 'answered': nothing was sent.

interface OpenQ {
  id:           string
  student_id:   string
  routed_to:    'mentor' | 'office'
  body:         string
  created_at:   string
  student_name: string
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function StudentQuestionsPanel() {
  const { profile } = useAuth()
  const role = profile?.role
  const isOffice = role === 'admin' || role === 'office_manager'
  const isMentor = role === 'instructor' || role === 'mentor'
  const [items, setItems]   = useState<OpenQ[]>([])
  const [loading, setLoading] = useState(true)
  const [active, setActive] = useState<OpenQ | null>(null)
  const [dismissing, setDismissing] = useState<OpenQ | null>(null)
  const [dismissBusy, setDismissBusy] = useState(false)
  const [dismissErr, setDismissErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!profile) return
    setLoading(true)
    const { data: rows } = await supabase
      .from('student_message')
      .select('id, student_id, routed_to, body, created_at')
      .eq('direction', 'student_to_staff')
      .eq('status', 'open')
      .order('created_at', { ascending: false })

    let filtered = (rows ?? []) as Omit<OpenQ, 'student_name'>[]
    if (isOffice) {
      filtered = filtered.filter(r => r.routed_to === 'office')
    } else if (isMentor) {
      // Students this mentor is responsible for — directly (student_mentor) or
      // through a class they mentor (class_mentor → class_enrollment).
      const mine = new Set<string>()
      const { data: sm } = await supabase
        .from('student_mentor').select('student_id').eq('mentor_id', profile.user_id).eq('status', 'active')
      ;(sm ?? []).forEach((r: { student_id: string }) => mine.add(r.student_id))
      const { data: cm } = await supabase
        .from('class_mentor').select('class_id').eq('user_id', profile.user_id)
      const classIds = (cm ?? []).map((r: { class_id: string }) => r.class_id)
      if (classIds.length) {
        const { data: enr } = await supabase
          .from('class_enrollment').select('student_id').in('class_id', classIds).eq('status', 'active')
        ;(enr ?? []).forEach((r: { student_id: string }) => mine.add(r.student_id))
      }
      filtered = filtered.filter(r => r.routed_to === 'mentor' && mine.has(r.student_id))
    } else {
      filtered = []
    }

    const studentIds = Array.from(new Set(filtered.map(r => r.student_id)))
    const { data: sps } = studentIds.length
      ? await supabase.from('student_profile').select('student_id, user_id').in('student_id', studentIds)
      : { data: [] as { student_id: string; user_id: string }[] }
    const uidByStudent = Object.fromEntries((sps ?? []).map(s => [s.student_id, s.user_id]))
    const uids = Array.from(new Set(Object.values(uidByStudent)))
    const { data: users } = uids.length
      ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', uids)
      : { data: [] as { user_id: string; first_name: string; last_name: string }[] }
    const nameByUid = Object.fromEntries((users ?? []).map(u => [u.user_id, `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()]))

    setItems(filtered.map(r => ({ ...r, student_name: nameByUid[uidByStudent[r.student_id]] || 'Student' })))
    setLoading(false)
  }, [profile, isOffice, isMentor])

  useEffect(() => { load() }, [load])

  async function confirmDismiss() {
    if (!dismissing) return
    setDismissBusy(true); setDismissErr(null)
    /* select() so a row blocked by RLS comes back empty rather than looking
       like a success — an update that matches nothing reports no error. */
    const { data, error } = await supabase
      .from('student_message')
      .update({ status: 'dismissed' })
      .eq('id', dismissing.id)
      .eq('status', 'open')
      .select('id')
    setDismissBusy(false)
    if (error)              { setDismissErr(error.message); return }
    if (!data || !data.length) { setDismissErr('Could not dismiss — the message may already be handled.'); return }
    setDismissing(null)
    load()
  }

  if (!isOffice && !isMentor) return null
  if (!loading && items.length === 0) return null   // only show when there's something to answer

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <QuestionAnswerIcon color="info" fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Student questions</Typography>
          {items.length > 0 && <Chip size="small" color="info" label={items.length} />}
        </Box>

        {loading ? (
          <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map(m => (
              <Box key={m.id} sx={{
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2,
                border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 1.75, py: 1.25,
              }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {m.student_name} <Chip size="small" variant="outlined" label={m.routed_to === 'office' ? 'to office' : 'to mentor'} sx={{ ml: 0.5, height: 18 }} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>{m.body}</Typography>
                  <Typography variant="caption" color="text.disabled">{fmt(m.created_at)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                  <Button size="small" color="inherit" onClick={() => { setDismissErr(null); setDismissing(m) }}>Dismiss</Button>
                  <Button size="small" variant="contained" onClick={() => setActive(m)}>Reply</Button>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>

      <ReplyDialog question={active} onClose={() => setActive(null)} onSent={() => { setActive(null); load() }} />

      <Dialog open={!!dismissing} onClose={() => !dismissBusy && setDismissing(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Dismiss this question?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            It leaves your list without a reply being sent. {dismissing?.student_name} is told nothing,
            so only dismiss what has already been handled somewhere else.
          </Typography>
          {dismissing && (
            <Box sx={{ background: '#F4EFE6', borderRadius: 2, p: 1.5 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{dismissing.body}</Typography>
            </Box>
          )}
          {dismissErr && <Alert severity="error" sx={{ mt: 1.5 }}>{dismissErr}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDismissing(null)} disabled={dismissBusy}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmDismiss} disabled={dismissBusy}
            startIcon={dismissBusy ? <CircularProgress size={14} color="inherit" /> : undefined}>
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

function ReplyDialog({ question, onClose, onSent }: {
  question: OpenQ | null
  onClose:  () => void
  onSent:   () => void
}) {
  const [reply, setReply] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { setReply(''); setError(null) }, [question?.id])

  async function sendReply(text: string): Promise<string | null> {
    if (!question) return 'No question selected.'
    const { data, error: e } = await supabase.functions.invoke('staff-reply-student', {
      body: { message_id: question.id, body: text },
    })
    if (e) return 'Could not send — please try again.'
    if (data?.error) return data.error
    onSent()
    return null
  }

  return (
    <Dialog open={!!question} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Reply to {question?.student_name}
        <Typography component="span" color="text.secondary" sx={{ fontWeight: 400 }}> · {question?.routed_to === 'office' ? 'to the office' : 'to their mentor'}</Typography>
      </DialogTitle>
      <DialogContent>
        {question && (
          <Box sx={{ background: '#F4EFE6', borderRadius: 2, p: 1.5, mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Their question</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{question.body}</Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <PolishComposer
          value={reply}
          onChange={setReply}
          onSend={sendReply}
          placeholder="Write your reply, or let AI polish it…"
          sendLabel="Send reply"
          rows={7}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  )
}
